import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { parseCookies } from '../utils/helperFunctions';

type GlobalContextValue = {
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  loadSiteName: () => void;
  colorMode: 'light' | 'dark';
  siteName: string;
  setSiteName: (name: string) => void;
  toggleColorMode: () => void;
  portalContainerRef: React.RefObject<HTMLElement | null>;
};

const GlobalContext = createContext<GlobalContextValue | null>(null);

export const GlobalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [siteName, setSiteName] = useState('');
  const [colorMode, setColorMode] = useState<'light' | 'dark'>('light');
  const portalContainerRef = useRef<HTMLElement | null>(null);

  const loadThemePreference = () => {
    const theme = localStorage.getItem('theme');
    if (theme) {
      setColorMode(theme as 'light' | 'dark');
    }
  };

  useEffect(() => {
    loadSiteName();
    loadThemePreference();

    portalContainerRef.current =
      document.getElementById('portal-root') ?? document.getElementById('root') ?? document.body;
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', colorMode === 'dark');
  }, [colorMode]);

  const loadSiteName = () => {
    const raw = parseCookies(document.cookie).site;
    if (!raw || siteName) {
      return;
    }
    setSiteName(decodeURIComponent(raw));
  };

  const toggleColorMode = () => {
    setColorMode(prev => (prev === 'light' ? 'dark' : 'light'));
    localStorage.setItem('theme', colorMode === 'light' ? 'dark' : 'light');
  };

  const value = useMemo(
    () => ({
      siteName,
      setSiteName,
      isLoading,
      setIsLoading,
      colorMode,
      toggleColorMode,
      portalContainerRef,
    }),
    [isLoading, colorMode, siteName]
  );

  return React.createElement(GlobalContext.Provider, { value: value as GlobalContextValue }, children);
};

export const useGlobalContext = (): GlobalContextValue => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('useGlobalContext must be used within a GlobalProvider');
  }
  return context;
};
